import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import type { AnalyticsTrackFn } from './types.js';

const createId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const createPermissionsModalInstanceId = (): string => createId();

export interface PermissionsModalContext {
  modal_instance_id: string;
  surface: string;
  permissions_modal_count?: number;
}

const withContext = (
  context: PermissionsModalContext,
  properties: EventProperties = {}
): EventProperties => ({
  ...properties,
  surface: context.surface,
  modal_instance_id: context.modal_instance_id,
  ...(context.permissions_modal_count !== undefined
    ? { permissions_modal_count: context.permissions_modal_count }
    : {})
});

export interface PermissionsErrorProperties extends EventProperties {
  permission_key?: string;
}

export const createPermissionsLifecycleTracker = (track: AnalyticsTrackFn) => ({
  trackOpened: (context: PermissionsModalContext, properties?: EventProperties): void => {
    track(
      EVENT_NAMES.PERMISSIONS_MODAL_OPENED,
      EVENT_CATEGORIES.ONBOARDING,
      withContext(context, properties)
    );
  },

  trackClosed: (context: PermissionsModalContext, properties?: EventProperties): void => {
    track(
      EVENT_NAMES.PERMISSIONS_MODAL_CLOSED,
      EVENT_CATEGORIES.ONBOARDING,
      withContext(context, properties)
    );
  },

  trackError: (
    context: PermissionsModalContext,
    properties: PermissionsErrorProperties = {}
  ): void => {
    // Historical data keeps the old name; new builds send only the canonical terminal.
    track(
      EVENT_NAMES.PERMISSIONS_GRANT_FAILED,
      EVENT_CATEGORIES.ONBOARDING,
      withContext(context, properties)
    );
  },

  // Net-new canonical terminal (ENG-4307): the permissions_modal_* family had no clean
  // success event. The modal fires this exactly once, only on a genuine grant.
  trackGrantSucceeded: (context: PermissionsModalContext, properties?: EventProperties): void => {
    track(
      EVENT_NAMES.PERMISSIONS_GRANT_SUCCEEDED,
      EVENT_CATEGORIES.ONBOARDING,
      withContext(context, properties)
    );
  },

  trackContinue: (context: PermissionsModalContext, properties?: EventProperties): void => {
    track(
      EVENT_NAMES.PERMISSIONS_MODAL_CONTINUE,
      EVENT_CATEGORIES.ONBOARDING,
      withContext(context, properties)
    );
  },

  trackSkip: (context: PermissionsModalContext, properties?: EventProperties): void => {
    track(
      EVENT_NAMES.PERMISSIONS_MODAL_SKIP,
      EVENT_CATEGORIES.ONBOARDING,
      withContext(context, properties)
    );
  },

  trackPermissionAction: (context: PermissionsModalContext, properties?: EventProperties): void => {
    track(
      EVENT_NAMES.PERMISSIONS_MODAL_PERMISSION_ACTION,
      EVENT_CATEGORIES.ONBOARDING,
      withContext(context, properties)
    );
  }
});
